import axios from 'axios';
import { apiRoute } from '../constants/api-constants';

/**
 * LOBBERO IQ IN THE RESIDENT PORTAL (backend migration 131,
 * src/CRUD/tenant-iq.js).
 *
 * Like tenant-client.ts, nothing here takes a unit, building or person: the
 * request is ONE string, the question, and the server answers for the session's
 * own home and nobody else's. Do not add an id to these calls — the server
 * would not read it, and the day it did would be the day a resident could ask
 * about a neighbour.
 *
 * The default axios instance, so every call carries the session cookies and the
 * CSRF header from axios-setup.ts.
 */

export interface ResidentIqItem {
  title: string;
  detail: string;
}

export interface ResidentIqLink {
  label: string;
  to: string;
}

/** Carries the reader's sentence to the pass form, which fills itself from it. */
export interface ResidentIqAddPass {
  kind: 'add_pass';
  sentence: string;
}

/**
 * A change to one of the reader's OWN passes, offered as a button. Lobbero IQ
 * never makes the change: pressing the button sends it through the pass
 * routes this portal already uses (tenant-passes-client passAction).
 */
export interface ResidentIqPassAction {
  kind: 'pass_action';
  pass_id: string;
  action: 'pause' | 'resume' | 'revoke' | 'skip' | 'unskip';
  /** 'YYYY-MM-DD' in the building's zone, for skip / unskip. */
  date: string | null;
  /** The button's words, e.g. "Pause Maria's pass". */
  label: string;
  /** What is said once it has been done. */
  done: string;
}

/** Something the answer offers to do — always by the reader's own press. */
export type ResidentIqAction = ResidentIqAddPass | ResidentIqPassAction;

const PASS_ACTIONS = ['pause', 'resume', 'revoke', 'skip', 'unskip'];

const readAction = (raw: unknown): ResidentIqAction | null => {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Record<string, unknown>;
  if (a.kind === 'add_pass' && typeof a.sentence === 'string') {
    return { kind: 'add_pass', sentence: a.sentence };
  }
  if (
    a.kind === 'pass_action' &&
    typeof a.pass_id === 'string' &&
    typeof a.action === 'string' &&
    PASS_ACTIONS.includes(a.action) &&
    typeof a.label === 'string'
  ) {
    return {
      kind: 'pass_action',
      pass_id: a.pass_id,
      action: a.action as ResidentIqPassAction['action'],
      date: typeof a.date === 'string' ? a.date : null,
      label: a.label,
      done: typeof a.done === 'string' ? a.done : 'Done.',
    };
  }

  return null;
};

export interface ResidentIqAnswer {
  available: boolean;
  topic: string;
  answer: string;
  items: ResidentIqItem[];
  link: ResidentIqLink | null;
  action: ResidentIqAction | null;
  period: string;
  reason: string;
}

export interface ResidentIqAvailability {
  available: boolean;
  examples: string[];
}

/** The server's own cap (src/resident-ask/intent.js MAX_QUESTION_CHARS). */
export const RESIDENT_IQ_MAX_CHARS = 400;

/**
 * May the launcher be shown? Off unless the organisation has Lobbero IQ and the
 * resident switch is on. Never throws: "not available" is the answer to every
 * failure, so a portal with IQ switched off simply has no button.
 */
export const residentIqAvailability = async (): Promise<ResidentIqAvailability> => {
  try {
    const { data } = await axios.get(`${apiRoute}/tenant/iq`);

    return {
      available: data?.available === true,
      examples: Array.isArray(data?.examples) ? (data.examples as string[]) : [],
    };
  } catch {
    return { available: false, examples: [] };
  }
};

/** Ask one question. Rejects with the axios error on a failed request. */
export const askResidentIq = async (question: string): Promise<ResidentIqAnswer> => {
  const { data } = await axios.post(`${apiRoute}/tenant/iq/ask`, { question });

  return {
    available: data?.available !== false,
    topic: typeof data?.topic === 'string' ? data.topic : '',
    answer: typeof data?.answer === 'string' ? data.answer : '',
    items: Array.isArray(data?.items) ? (data.items as ResidentIqItem[]) : [],
    link:
      data?.link && typeof data.link.to === 'string'
        ? (data.link as ResidentIqLink)
        : null,
    action: readAction(data?.action),
    period: typeof data?.period === 'string' ? data.period : '',
    reason: typeof data?.reason === 'string' ? data.reason : '',
  };
};
